import React, { useMemo, useState, useEffect } from 'react';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon, CheckCircleIcon, XCircleIcon, LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { computeLoanTermYears, calcGuaranteeFee, calcAnnualPmt, calcDSCR, calcProjectCost } from '../../utils/loanMath';

const PrequalificationTable = ({ ocrData = {}, loanData = {}, isLocked = true, onUnlockRequest }) => {
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [lastSavedScenario, setLastSavedScenario] = useState(null);
  const [isEditingPrequalAmount, setIsEditingPrequalAmount] = useState(false);
  const [newPrequalAmount, setNewPrequalAmount] = useState('');

  // Initialize selected scenario based on existing prequalification data
  useEffect(() => {
    if (loanData && loanData.prequalificationType) {
      setSelectedScenario(loanData.prequalificationType);
      setLastSavedScenario(loanData.prequalificationType);
    }
  }, [loanData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
  };

  // Sort years chronologically
  const years = useMemo(() => {
    return Object.keys(ocrData).sort((a, b) => {
      const order = {
        'Business Tax Return - 2 Years Ago': 0,
        'Business Tax Return - Last Year': 1,
        'Business Tax Return - Current Year': 2
      };
      return order[a] - order[b];
    });
  }, [ocrData]);

  // Calculate EBITDA + Owner's Comp
  const calculateEBITDAOwnersComp = (yearData) => {
    if (!yearData) return 0;
    
    // Get base income
    const baseIncome = Number(yearData.ordinary_business_income || 0);
    
    // Get officer compensation
    const originalOfficerCompensation = Number(yearData.compensation_of_officers || 0);
    
    // Get interest, depreciation, and amortization
    const interest = Number(yearData.interest || 0);
    const depreciation = Number(yearData.depreciation || 0);
    const amortization = Number(yearData.amortization || 0);
    
    // Calculate EBITDA + Owner's Comp
    const result = baseIncome + originalOfficerCompensation + interest + depreciation + amortization;
    
    return result;
  };

  // Calculate Add-Backs
  const calculateAddBacks = (otherDeductions) => {
    if (Array.isArray(otherDeductions)) {
      return otherDeductions.reduce((total, deduction) => {
        return total + Number(deduction.amount || 0);
      }, 0);
    }
    
    if (typeof otherDeductions === 'object' && otherDeductions !== null) {
      return Object.entries(otherDeductions).reduce((total, [category, amount]) => {
        return total + Number(amount || 0);
      }, 0);
    }
    
    return 0;
  };

  // Calculate SDE
  const calculateSDE = (yearData) => {
    if (!yearData) return 0;
    const ebitdaOwnersComp = calculateEBITDAOwnersComp(yearData);
    const addbacksTotal = calculateAddBacks(yearData.other_deductions || []);
    
    return ebitdaOwnersComp + addbacksTotal;
  };

  // Calculate NOI with optional salary parameter
  const calculateNOI = (yearData, newOfficerSalary = 75000) => {
    if (!yearData) return 0;
    const sde = calculateSDE(yearData);
    return sde - newOfficerSalary;
  };

  // Interest rate range: Prime (6.75%) + 2% to Prime + 2.75%
  const LOW_RATE = 0.095;   // 9.5%
  const HIGH_RATE = 0.1025; // 10.25%

  // Calculate Annual Debt Service (returns high estimate for DSCR scenarios)
  const calculateAnnualDebtService = (purchasePrice, equityPct = 10) => {
    const { loanAmount } = calcProjectCost(purchasePrice, { equityPercent: equityPct });
    const realEstateValue = Number(loanData.realEstateValue || 0);
    const termYears = computeLoanTermYears({ purchasePrice: Number(purchasePrice), realEstateValue });

    if (loanAmount <= 0) return 0;

    return calcAnnualPmt({ principal: loanAmount, rate: HIGH_RATE, termYears });
  };

  // Calculate debt service range (low and high)
  const calculateDebtServiceRange = (purchasePrice, equityPct = 10) => {
    const { loanAmount } = calcProjectCost(purchasePrice, { equityPercent: equityPct });
    const realEstateValue = Number(loanData.realEstateValue || 0);
    const termYears = computeLoanTermYears({ purchasePrice: Number(purchasePrice), realEstateValue });

    if (loanAmount <= 0) return { low: 0, high: 0 };

    return {
      low: calcAnnualPmt({ principal: loanAmount, rate: LOW_RATE, termYears }),
      high: calcAnnualPmt({ principal: loanAmount, rate: HIGH_RATE, termYears })
    };
  };

  // Calculate DSCR for a specific year and purchase price with optional salary parameter
  const calculateDSCR = (yearData, purchasePrice, newOfficerSalary = 75000) => {
    if (!yearData) return 0;
    
    const noi = calculateNOI(yearData, newOfficerSalary);
    const annualDebtService = calculateAnnualDebtService(purchasePrice);
        
    return calcDSCR({ noi, annualDebtService });
  };

  // Find maximum purchase price that meets DSCR threshold for all years with optional salary parameter
  const findMaxPurchasePrice = (dscrThreshold, newOfficerSalary = 75000) => {
    if (years.length === 0) return 0;
    
    // Start with a low value and increment until we find the maximum
    let currentPrice = 10000; // Start at $10k
    let step = 10000;
    let maxPrice = 0;
    
    // Try increasing prices until we find the maximum that works
    while (currentPrice <= 10000000) { // Cap at $10M to prevent infinite loops
      let allYearsMeetThreshold = true;
      
      // Check if all years meet the DSCR threshold at current price
      for (const year of years) {
        const yearData = ocrData[year];
        const dscr = calculateDSCR(yearData, currentPrice, newOfficerSalary);
        
        if (dscr < dscrThreshold) {
          allYearsMeetThreshold = false;
          break;
        }
      }
      
      if (allYearsMeetThreshold) {
        // This price works, save it and try higher
        maxPrice = currentPrice;
        currentPrice += step;
      } else {
        // If we've already found a working price and now hit a failure,
        // we can do a more precise search
        if (maxPrice > 0) {
          // Go back to the last working price
          currentPrice = maxPrice;
          // Use a smaller step for more precision
          step = step / 2;
          
          // If we're down to $1000 steps, we're precise enough
          if (step < 1000) {
            break;
          }
          
          // Try again with the smaller step
          currentPrice += step;
        } else {
          // We haven't found a working price yet, try a lower price
          currentPrice = Math.max(10000, currentPrice - step);
          // Use a smaller step
          step = step / 2;
          
          // If we're down to very small steps and still haven't found anything,
          // there might not be a viable price
          if (step < 1000) {
            return 0; // No viable price found
          }
        }
      }
    }
    
    return maxPrice;
  };

  // Find maximum purchase price for most recent year at target DSCR (90/10 structure)
  const findMaxPriceRecentYearOnly = (dscrThreshold) => {
    if (years.length === 0) return 0;

    const mostRecentYear = years[years.length - 1];
    const recentYearData = ocrData[mostRecentYear];

    if (!recentYearData) return 0;

    // Start with a low value and increment until we find the maximum
    let currentPrice = 10000;
    let step = 10000;
    let maxPrice = 0;

    while (currentPrice <= 10000000) {
      const dscr = calculateDSCR(recentYearData, currentPrice, 0); // No salary deduction

      if (dscr >= dscrThreshold) {
        maxPrice = currentPrice;
        currentPrice += step;
      } else {
        if (maxPrice > 0) {
          currentPrice = maxPrice;
          step = step / 2;
          if (step < 1000) break;
          currentPrice += step;
        } else {
          currentPrice = Math.max(10000, currentPrice - step);
          step = step / 2;
          if (step < 1000) return 0;
        }
      }
    }

    return maxPrice;
  };

  // Calculate debt service for a given equity percentage of purchase price
  const calculateDebtServiceForLoanPercent = (purchasePrice, loanPercent) => {
    const equityPct = 100 - loanPercent;
    const { loanAmount } = calcProjectCost(purchasePrice, { equityPercent: equityPct });

    const realEstateValue = Number(loanData.realEstateValue || 0);
    const termYears = computeLoanTermYears({ purchasePrice, realEstateValue });

    return calcAnnualPmt({ principal: loanAmount, rate: HIGH_RATE, termYears });
  };

  // Calculate required equity injection to hit target DSCR at current purchase price
  // Uses binary search to find the minimum loan percentage that achieves target DSCR
  const calculateRequiredEquityInjection = (dscrThreshold) => {
    if (years.length === 0) return { equityPercent: 10, loanAmount: 0, canAchieve: false };

    const mostRecentYear = years[years.length - 1];
    const recentYearData = ocrData[mostRecentYear];
    const currentPurchasePrice = Number(loanData.purchasePrice || loanData.projectedSalePrice || 0);

    if (!recentYearData || currentPurchasePrice <= 0) {
      return { equityPercent: 10, loanAmount: 0, canAchieve: false };
    }

    // Calculate NOI for most recent year
    const noi = calculateNOI(recentYearData, 0);

    if (noi <= 0) {
      return { equityPercent: 100, loanAmount: 0, canAchieve: false };
    }

    // Binary search to find the maximum loan percentage that achieves target DSCR
    // Start at 90% (standard) and work down
    let minLoanPercent = 10;  // Minimum we'd consider
    let maxLoanPercent = 90;  // Standard SBA is 90%
    let bestLoanPercent = 10; // Start with worst case

    // First check if 90% works
    const debtServiceAt90 = calculateDebtServiceForLoanPercent(currentPurchasePrice, 90);
    const dscrAt90 = debtServiceAt90 > 0 ? noi / debtServiceAt90 : 0;

    if (dscrAt90 >= dscrThreshold) {
      // 90/10 works!
      const proj = calcProjectCost(currentPurchasePrice, { equityPercent: 10 });
      return {
        equityPercent: 10,
        loanPercent: 90,
        loanAmount: proj.loanAmount,
        equityAmount: proj.equity,
        canAchieve: true
      };
    }

    // Binary search to find the right loan percentage
    while (maxLoanPercent - minLoanPercent > 1) {
      const midLoanPercent = Math.floor((minLoanPercent + maxLoanPercent) / 2);
      const debtService = calculateDebtServiceForLoanPercent(currentPurchasePrice, midLoanPercent);
      const dscr = debtService > 0 ? noi / debtService : 0;

      if (dscr >= dscrThreshold) {
        // This loan percent works, try higher
        bestLoanPercent = midLoanPercent;
        minLoanPercent = midLoanPercent;
      } else {
        // Too much loan, need more equity
        maxLoanPercent = midLoanPercent;
      }
    }

    // Fine-tune by checking the edges
    for (let lp = maxLoanPercent; lp >= minLoanPercent; lp--) {
      const debtService = calculateDebtServiceForLoanPercent(currentPurchasePrice, lp);
      const dscr = debtService > 0 ? noi / debtService : 0;
      if (dscr >= dscrThreshold) {
        bestLoanPercent = lp;
        break;
      }
    }

    const equityPercent = 100 - bestLoanPercent;
    const proj = calcProjectCost(currentPurchasePrice, { equityPercent });

    return {
      equityPercent: equityPercent,
      loanPercent: bestLoanPercent,
      loanAmount: proj.loanAmount,
      equityAmount: proj.equity,
      canAchieve: equityPercent <= 50
    };
  };

  // Get most recent year label for display
  const mostRecentYearLabel = years.length > 0 ? years[years.length - 1].replace('Business Tax Return - ', '') : 'Most Recent Year';

  // Calculate the new financing scenarios
  const adjustedPriceScenario = {
    name: "Adjusted Purchase Price",
    description: `${mostRecentYearLabel} DSCR ≥ 1.25 (90% SBA / 10% Equity)`,
    threshold: 1.25,
    maxPrice: findMaxPriceRecentYearOnly(1.25),
    equityPercent: 10,
    icon: <CheckCircleIcon className="h-5 w-5 text-blue-500" />,
    color: "bg-blue-100 text-blue-800"
  };

  const equityInjectionData = calculateRequiredEquityInjection(1.25);
  const currentPurchasePrice = Number(loanData.purchasePrice || loanData.projectedSalePrice || 0);

  const raisedEquityScenario = {
    name: "Raised Equity Injection",
    description: `${mostRecentYearLabel} DSCR ≥ 1.25 at current price (${equityInjectionData.loanPercent}% SBA / ${equityInjectionData.equityPercent}% Equity)`,
    threshold: 1.25,
    maxPrice: currentPurchasePrice,
    equityPercent: equityInjectionData.equityPercent,
    loanPercent: equityInjectionData.loanPercent,
    equityAmount: equityInjectionData.equityAmount,
    loanAmount: equityInjectionData.loanAmount,
    canAchieve: equityInjectionData.canAchieve,
    icon: <ArrowTrendingUpIcon className="h-5 w-5 text-purple-500" />,
    color: "bg-purple-100 text-purple-800",
    note: equityInjectionData.equityPercent > 10 ? `Requires ${equityInjectionData.equityPercent}% equity injection` : null
  };

  const financingScenarios = [adjustedPriceScenario, raisedEquityScenario];

  // Calculate current DSCR values for reference
  const currentDSCRs = years.map(year => {
    const yearData = ocrData[year];
    const currentPrice = Number(loanData.purchasePrice || 0);
    return {
      year: year.replace('Business Tax Return - ', ''),
      dscr: calculateDSCR(yearData, currentPrice)
    };
  });

  const handleSelectScenario = async (scenario) => {
    // If locked, don't allow selection
    if (isLocked) {
      if (onUnlockRequest && typeof onUnlockRequest === 'function') {
        onUnlockRequest();
      }
      return;
    }

    const scenarioId = scenario.name;
    setSelectedScenario(scenarioId);

    // Save to Firestore if we have a prequalId
    if (loanData.id) {
      try {
        setIsSaving(true);
        const prequalRef = doc(db, 'prequalificationApplications', loanData.id);

        await updateDoc(prequalRef, {
          prequalifiedAmount: scenario.maxPrice,
          prequalificationType: scenario.name,
          prequalificationEquityPercent: scenario.equityPercent || 10,
          status: 'prequalified',
          lastUpdated: new Date()
        });

        setLastSavedScenario(scenarioId);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } catch (error) {
        console.error("Error saving prequalification:", error);
        alert("Failed to save prequalification. Please try again.");
      } finally {
        setIsSaving(false);
      }
    }
  };

  // When the component is locked, reset to the last saved scenario
  useEffect(() => {
    if (isLocked && lastSavedScenario) {
      setSelectedScenario(lastSavedScenario);
    }
  }, [isLocked, lastSavedScenario]);

  const handleSavePrequalAmount = async () => {
    try {
      setIsSaving(true);
      
      // Use newPrequalAmount instead of amount
      const amount = parseFloat(newPrequalAmount);
      if (isNaN(amount) || amount <= 0) {
        alert('Please enter a valid amount');
        return;
      }
      
      // Update prequalification application
      const prequalDocRef = doc(db, 'prequalificationApplications', loanData.id);
      await updateDoc(prequalDocRef, {
        prequalifiedAmount: amount,
        lastUpdated: serverTimestamp(),
        // Add any other relevant fields
      });
      
      // Update metadata if needed
      const metadataRef = collection(db, 'prequalificationMetadata');
      const metadataQuery = query(metadataRef, 
        where('applicationId', '==', loanData.id)
      );
      const metadataSnap = await getDocs(metadataQuery);
      
      // Update relevant metadata documents
      const updatePromises = metadataSnap.docs.map(doc => 
        updateDoc(doc.ref, {
          // Add relevant updates
        })
      );
      
      await Promise.all(updatePromises);
      
      setIsEditingPrequalAmount(false);
      setNewPrequalAmount(''); // Reset the amount input
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white shadow-lg rounded-lg overflow-hidden">
      {saveSuccess && (
        <div className="m-4 p-2 bg-green-100 text-green-800 rounded-md text-center">
          Prequalification amount saved successfully!
        </div>
      )}
      
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium leading-6 text-gray-900">Prequalification Analysis</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Based on financial performance, here are potential financing scenarios.
          </p>
        </div>
        <div className="flex items-center text-sm text-gray-500">
          {isLocked ? (
            <>
              <LockClosedIcon className="h-4 w-4 mr-1 text-gray-400" />
              <span>Locked</span>
            </>
          ) : (
            <>
              <LockOpenIcon className="h-4 w-4 mr-1 text-green-500" />
              <span className="text-green-600">Unlocked</span>
            </>
          )}
        </div>
      </div>
      
      {/* Current DSCR Values */}
      <div className="px-4 py-3 sm:px-6 bg-gray-50">
        <h4 className="text-sm font-medium text-gray-700">Current DSCR Values at {formatCurrency(Number(loanData.purchasePrice || 0))}</h4>
        <div className="mt-2 flex flex-wrap gap-2">
          {currentDSCRs.map(item => (
            <span key={item.year} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
              {item.year}: {formatNumber(item.dscr)}
            </span>
          ))}
        </div>
      </div>
      
      {/* Financing Scenarios */}
      <div className="border-t border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h4 className="text-sm font-medium text-gray-700 mb-4">Financing Options (Based on {mostRecentYearLabel} at 1.25 DSCR)</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {financingScenarios.map((scenario) => {
              const scenarioId = scenario.name;
              const isSelected = selectedScenario === scenarioId;
              const isNotAchievable = scenario.name === "Raised Equity Injection" && !scenario.canAchieve;

              return (
                <div
                  key={scenario.name}
                  className={`relative rounded-lg border ${isSelected ? 'border-green-500 bg-green-50' : isNotAchievable ? 'border-red-200 bg-red-50' : 'border-gray-300 bg-white'} px-6 py-5 shadow-sm flex items-center space-x-3 ${isLocked || isNotAchievable ? 'cursor-not-allowed opacity-80' : 'hover:border-gray-400 cursor-pointer'}`}
                  onClick={() => !isNotAchievable && handleSelectScenario(scenario)}
                >
                  <div className="flex-shrink-0">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${isNotAchievable ? 'bg-red-100' : scenario.color.split(' ')[0]}`}>
                      {isNotAchievable ? <XCircleIcon className="h-5 w-5 text-red-500" /> : scenario.icon}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="focus:outline-none">
                      <span className="absolute inset-0" aria-hidden="true" />
                      <p className="text-sm font-medium text-gray-900">{scenario.name}</p>
                      <p className="text-sm text-gray-500">{scenario.description}</p>
                      {scenario.note && (
                        <p className="text-xs text-amber-600 mt-1">{scenario.note}</p>
                      )}
                      {isNotAchievable && (
                        <p className="text-xs text-red-600 mt-1">Not achievable - requires too much equity</p>
                      )}
                      <div className="mt-2 space-y-1">
                        <p className="text-lg font-semibold text-gray-900">
                          {scenario.name === "Adjusted Purchase Price" ? "Max Price: " : "Price: "}
                          {formatCurrency(scenario.maxPrice)}
                        </p>
                        {scenario.name === "Adjusted Purchase Price" && (
                          <p className="text-sm text-gray-500">
                            Equity: {formatCurrency(calcProjectCost(scenario.maxPrice).equity)} (10% of total project cost)
                          </p>
                        )}
                        {scenario.name === "Raised Equity Injection" && scenario.equityAmount && (
                          <p className="text-sm text-gray-500">
                            Equity: {formatCurrency(calcProjectCost(scenario.maxPrice, { equityPercent: scenario.equityPercent }).equity)} ({scenario.equityPercent}% of total project cost)
                          </p>
                        )}
                      </div>
                      {isSelected && (
                        <p className="text-sm text-green-600 font-medium mt-1 flex items-center">
                          {isSaving ? "Saving..." : "Selected"}
                          {isLocked && <LockClosedIcon className="h-3 w-3 ml-1" />}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Detailed Analysis Table */}
      <div className="border-t border-gray-200">
        <div className="px-4 py-5 sm:p-6">
          <h4 className="text-sm font-medium text-gray-700 mb-1">Detailed Analysis</h4>
          <p className="text-xs text-gray-500 mb-4 italic">(Backed by lender. Subject to change per borrower strength)</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Scenario</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Purchase Price</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">SBA Loan Amount</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Equity Required</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Annual Debt Service</th>
                  {years.map(year => (
                    <th key={year} scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      DSCR {year.replace('Business Tax Return - ', '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {financingScenarios.map((scenario) => {
                  const eqPct = scenario.name === "Adjusted Purchase Price" ? 10 : (scenario.equityPercent || 10);
                  const project = calcProjectCost(scenario.maxPrice, { equityPercent: eqPct });

                  const realEstateValue = Number(loanData.realEstateValue || 0);
                  const termYears = computeLoanTermYears({ purchasePrice: scenario.maxPrice, realEstateValue });
                  // Use workbook debt service if available, otherwise calculate
                  const workbookDebtService = loanData.totalDebtService_2024 || loanData.annualDebtService;
                  const annualDebtServiceLow = workbookDebtService || calcAnnualPmt({ principal: project.loanAmount, rate: LOW_RATE, termYears });
                  const annualDebtServiceHigh = workbookDebtService || calcAnnualPmt({ principal: project.loanAmount, rate: HIGH_RATE, termYears });

                  return (
                    <tr key={scenario.name}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                        <div className="flex items-center">
                          <span className={`mr-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${scenario.color}`}>
                            {scenario.name}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {scenario.name === "Adjusted Purchase Price" ? "90% Loan / 10% Equity" : `${scenario.loanPercent}% Loan / ${scenario.equityPercent}% Equity`}
                        </p>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(scenario.maxPrice)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(project.loanAmount)}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{formatCurrency(project.equity)}</td>
                      <td className="px-3 py-4 text-sm text-gray-500">{workbookDebtService ? formatCurrency(workbookDebtService) : `${formatCurrency(annualDebtServiceLow)} - ${formatCurrency(annualDebtServiceHigh)}`}</td>
                      {years.map(year => {
                        const yearData = ocrData[year];
                        const noi = calculateNOI(yearData, 0);
                        const dscr = annualDebtServiceHigh > 0 ? noi / annualDebtServiceHigh : 0;
                        const meetsThreshold = dscr >= scenario.threshold;

                        return (
                          <td key={year} className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <span className={`font-medium ${meetsThreshold ? 'text-green-600' : 'text-red-600'}`}>
                              {formatNumber(dscr)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrequalificationTable;
